import { Typography, Box, Card } from "@mui/material";
import BenefitProgressCircle from "./shared/BenefitProgressCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FormattedCurrency from "components/WhenBenefit/FormattedCurrency";
import moment, { Moment } from "moment";

interface BenefitCardProps {
  month: string;
  year: string;
  expireDate: Moment;
  total: number;
  pending: number;
  reimbursed: number;
  remaining: number;
}

export const BenefitCard = ({ month, year, expireDate, total, pending, reimbursed, remaining }: BenefitCardProps) => {
  // Check if the expireDate is before today's date
  const isExpired = expireDate.isBefore(moment());

  const getStatusDisplay = () => {
    const iconTextColor = isExpired ? "text.secondary" : "#2A876C";
    const pillBgColor = isExpired ? "#FFFFFF" : "#0472521C";
    const textPrefix = isExpired ? `Expired ` : `Expires on: `;
    const dateText = expireDate.format("MMM DD, YYYY");
    const IconComponent = AccessTimeIcon;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          bgcolor: pillBgColor,
          px: 2,
          py: 1.5,
          borderRadius: "12px",
          color: iconTextColor,
        }}
      >
        <IconComponent sx={{ mr: 0.5, fontSize: "1rem" }} />
        <Typography variant="body2" sx={{ fontWeight: "normal", mr: 0.5 }}>
          {textPrefix}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          {dateText}
        </Typography>
      </Box>
    );
  };

  // Update colors based on actual expiration date
  const headerBgColor = isExpired ? "#f0f0f0" : "#e8f5f2";
  const progressColor = isExpired ? "#cccccc" : "#007c60";

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "visible",
        boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
        borderRadius: 2,
        borderBottom: "1px solid #e0e0e0",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          bgcolor: headerBgColor,
          p: 2,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontSize: "18px",
            fontWeight: "800",
            lineHeight: "21px",
            textTransform: "uppercase",
            color: isExpired ? "text.secondary" : "#2A876C", // Apply conditional color based on actual expiration
          }}
        >
          {month} {year}
        </Typography>
        <Box sx={{ mt: 1 }}>{getStatusDisplay()}</Box>
      </Box>
      <Box sx={{ p: 3, display: "flex", flexDirection: "column", flex: 1, bgcolor: "#ffffff" }}>
        <Box sx={{ display: "flex", justifyContent: "center", my: 1 }}>
          <BenefitProgressCircle value={remaining} total={total} color={progressColor} />
        </Box>
        <Box sx={{ pt: 5, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Box sx={{ width: "fit-content" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body1" sx={{ mr: 2 }}>
                Total When Benefit:{" "}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                <FormattedCurrency value={total} />
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body1">Pending:</Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                <FormattedCurrency value={pending} />
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body1">Reimbursed:</Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                <FormattedCurrency value={reimbursed} />
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body1">Remaining:</Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                <FormattedCurrency value={remaining} />
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};
